import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import { ContentCollection } from '../hooks/api/useContentCollection';
import { VideoExperienceTile, VideoExperienceTileSkeleton } from './video-experience-tile';
import { AudioExperienceTile, AudioExperienceTileSkeleton } from './audio-experience-tile';
import { ResourceTile, ResourceTileSkeleton } from './resource-tile';
import { ROUTE_PATH } from '../routes/route-paths';
import { Content, Resource } from '../hooks/api/useExperiences';
import { useMediaQuery } from '../hooks/utils/useMediaQuery';
import { TooltipIcon, ArrowButton } from '../assets/images/svgs';

interface CollectionSummaryProps {
  collection?: ContentCollection;
  collectionName: string;
  isPrimaryLandingPage?: boolean;
  selectedTagGroupName?: string;
  isLoading?: boolean;
}

const ArrowButtonWithHover: React.FC<{
  onClick: () => void;
  disabled: boolean;
  direction: 'left' | 'right';
  ariaLabel: string;
}> = ({ onClick, disabled, direction, ariaLabel }) => {
  const [isHovered, setIsHovered] = useState(false);

  const backgroundColor = disabled
    ? 'rgb(var(--color-gray-light))'
    : isHovered
      ? 'rgb(var(--color-button-secondary-hover))'
      : 'rgb(var(--color-button-secondary))';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex h-6 w-6 flex-shrink-0 items-center justify-center transition-colors ${
        disabled ? 'cursor-default' : 'cursor-pointer'
      }`}
      style={disabled ? { cursor: 'default' } : undefined} // to override the default disabled prop styling on <button/>
      aria-label={ariaLabel}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ArrowButton backgroundColor={backgroundColor} disabled={disabled} direction={direction} className="h-6 w-6" />
    </button>
  );
};

export const CollectionSummary: React.FC<CollectionSummaryProps> = ({
  collection,
  collectionName,
  isPrimaryLandingPage = true, // to be used for topic landing page to adjust titling
  selectedTagGroupName,
  isLoading = false,
}) => {
  const dfMdMedia = useMediaQuery('md');
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [collectionTooltipOpen, setCollectionTooltipOpen] = useState(false);
  const isVerticalCollection = collection?.verticals?.length && collection.verticals.length > 0;
  const tooltipTitle =
    isPrimaryLandingPage && !isVerticalCollection ? (
      <>
        This collection of content is shown based on your selected preferences. You can{' '}
        <Link className="text-link-light underline" to={ROUTE_PATH.TOPIC_PREFERENCES}>
          update your preferences
        </Link>{' '}
        at any time within your profile.
      </>
    ) : isPrimaryLandingPage && isVerticalCollection ? (
      <>
        This collection of content is shown based on your sponsoring organization. You can{' '}
        <Link className="text-link-light underline" to={ROUTE_PATH.TOPIC_PREFERENCES}>
          update your preferences
        </Link>{' '}
        at any time within your profile to view additional relevant collections.
      </>
    ) : (
      <>
        This topic page is a summary of this content collection. You can have this content appear on your home page by
        setting the topic as one of your preferences within your profile.
      </>
    );

  const displayItems = useMemo(() => {
    if (isLoading || !collection) return [];
    return collection.items
      .filter((item) => {
        return item.listener_audio_id || item.resource_id || (item.data as Content)?.video_id;
      })
      .slice(0, 10)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [collection, isLoading]);

  const hasMoreItems = isLoading ? false : (collection?.total_items_count ?? collection?.items.length ?? 0) > 10;

  // Skeleton pattern: audio, video, resource (repeating), ending with audio for 10 items total
  const skeletonItems = useMemo(() => {
    if (!isLoading) return [];
    const SKELETON_COUNT = 10;
    const BASE_PATTERN: ('audio' | 'video' | 'resource')[] = ['audio', 'video', 'resource'];
    return Array.from({ length: SKELETON_COUNT }, (_, index) => ({
      type: BASE_PATTERN[index % BASE_PATTERN.length] as 'audio' | 'video' | 'resource',
      index,
    }));
  }, [isLoading]);

  const getCarouselItems = (): HTMLElement[] => {
    if (!carouselRef.current) return [];
    return Array.from(carouselRef.current.querySelectorAll('[data-carousel-item]')) as HTMLElement[];
  };

  const getViewportDimensions = () => {
    if (!carouselRef.current) return null;
    const carousel = carouselRef.current;
    return {
      scrollLeft: carousel.scrollLeft,
      width: carousel.clientWidth,
      right: carousel.scrollLeft + carousel.clientWidth,
    };
  };

  const isItemVisible = (item: HTMLElement, viewportLeft: number, viewportRight: number): boolean => {
    const itemLeft = item.offsetLeft;
    const itemRight = itemLeft + item.offsetWidth;
    return itemLeft < viewportRight && itemRight > viewportLeft;
  };

  const isItemFullyVisible = (item: HTMLElement, viewportLeft: number, viewportRight: number): boolean => {
    const itemLeft = item.offsetLeft;
    const itemRight = itemLeft + item.offsetWidth;
    return itemLeft >= viewportLeft && itemRight <= viewportRight;
  };

  const findFirstVisibleItem = (items: HTMLElement[], viewportLeft: number, viewportRight: number): number => {
    for (let i = 0; i < items.length; i++) {
      if (isItemVisible(items[i], viewportLeft, viewportRight)) {
        return i;
      }
    }
    return -1;
  };

  const findRightmostVisibleItem = (
    items: HTMLElement[],
    viewportLeft: number,
    viewportRight: number,
  ): { item: HTMLElement; index: number } | null => {
    let rightmostItem: HTMLElement | null = null;
    let rightmostIndex = -1;
    let rightmostRight = viewportLeft;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (isItemVisible(item, viewportLeft, viewportRight)) {
        const itemRight = item.offsetLeft + item.offsetWidth;
        if (itemRight > rightmostRight) {
          rightmostItem = item;
          rightmostIndex = i;
          rightmostRight = itemRight;
        }
      }
    }

    return rightmostItem ? { item: rightmostItem, index: rightmostIndex } : null;
  };

  // Update scroll button states
  useEffect(() => {
    const updateScrollButtons = () => {
      if (!carouselRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };

    updateScrollButtons();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        carousel.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [displayItems.length]);

  const scrollLeft = () => {
    const items = getCarouselItems();
    const viewport = getViewportDimensions();
    if (!items.length || !viewport || !carouselRef.current) return;

    const firstVisibleIndex = findFirstVisibleItem(items, viewport.scrollLeft, viewport.right);

    if (firstVisibleIndex > 0) {
      carouselRef.current.scrollTo({ left: items[firstVisibleIndex - 1].offsetLeft, behavior: 'smooth' });
    } else if (firstVisibleIndex === 0) {
      carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const items = getCarouselItems();
    const viewport = getViewportDimensions();
    if (!items.length || !viewport || !carouselRef.current) return;

    const GAP = 24;
    const rightmost = findRightmostVisibleItem(items, viewport.scrollLeft, viewport.right);

    if (!rightmost || rightmost.index >= items.length - 1) {
      if (rightmost?.index === items.length - 1) {
        const lastItem = items[items.length - 1];
        const maxScroll = lastItem.offsetLeft + lastItem.offsetWidth - viewport.width;
        if (carouselRef.current.scrollLeft < maxScroll) {
          carouselRef.current.scrollTo({ left: Math.max(0, maxScroll), behavior: 'smooth' });
        }
      }
      return;
    }

    const nextItem = items[rightmost.index + 1];

    if (isItemFullyVisible(nextItem, viewport.scrollLeft, viewport.right)) {
      for (let i = rightmost.index + 2; i < items.length; i++) {
        const item = items[i];
        if (!isItemFullyVisible(item, viewport.scrollLeft, viewport.right)) {
          carouselRef.current.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
          return;
        }
      }
      const lastItem = items[items.length - 1];
      const maxScroll = lastItem.offsetLeft + lastItem.offsetWidth - viewport.width;
      carouselRef.current.scrollTo({ left: Math.max(0, maxScroll), behavior: 'smooth' });
    } else {
      carouselRef.current.scrollBy({ left: rightmost.item.offsetWidth + GAP, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative w-full">
      <h3 className="font-albert-sans text-section-header-slate/80 mb-4 text-base font-semibold uppercase leading-[18px] tracking-wide">
        Collection
      </h3>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2
            className={`font-albert-sans ${dfMdMedia ? 'text-[28px]' : 'text-[22px]'} font-semibold leading-7 text-black`}
          >
            {collectionName}
          </h2>
          <ClickAwayListener onClickAway={() => !dfMdMedia && setCollectionTooltipOpen(false)}>
            <Tooltip
              title={tooltipTitle}
              arrow
              open={!dfMdMedia ? collectionTooltipOpen : undefined}
              onClose={!dfMdMedia ? () => setCollectionTooltipOpen(false) : undefined}
              disableFocusListener={!dfMdMedia}
              disableHoverListener={!dfMdMedia}
              disableTouchListener={!dfMdMedia}
              disableInteractive={false}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'rgb(var(--color-black))',
                    color: '#FFFFFF',
                    fontFamily: 'Open Sans, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    padding: '8px 12px',
                    borderRadius: '4px',
                    pointerEvents: 'auto',
                    cursor: 'default',
                    '& .MuiTooltip-arrow': {
                      color: '#000000',
                    },
                  },
                },
              }}
            >
              <button
                type="button"
                onClick={() => !dfMdMedia && setCollectionTooltipOpen((o) => !o)}
                className="focus-visible:ring-primary inline-flex min-h-[48px] min-w-[48px] cursor-pointer items-center justify-center rounded opacity-70 transition-opacity duration-200 hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                aria-label={typeof tooltipTitle === 'string' ? tooltipTitle : 'Collection information'}
                aria-expanded={!dfMdMedia ? collectionTooltipOpen : undefined}
              >
                <img src={TooltipIcon} alt="" className="pointer-events-none h-[18px] w-[18px]" />
              </button>
            </Tooltip>
          </ClickAwayListener>
        </div>
        {dfMdMedia && (
          <div className="flex items-center justify-end gap-6">
            <div className="flex items-center gap-2">
              <ArrowButtonWithHover
                onClick={scrollLeft}
                disabled={!canScrollLeft || isLoading}
                direction="left"
                ariaLabel="Scroll left"
              />
              <ArrowButtonWithHover
                onClick={scrollRight}
                disabled={!canScrollRight || isLoading}
                direction="right"
                ariaLabel="Scroll right"
              />
            </div>
            {hasMoreItems && !isLoading && collection && (
              <Link to={`${ROUTE_PATH.COLLECTION}/${collection.id}`} className="text-primary flex items-center gap-1">
                <span className="font-albert-sans text-lg font-medium">View More</span>
                <ArrowButton direction="right" className="h-6 w-6" />
              </Link>
            )}
          </div>
        )}
      </div>
      {(displayItems.length > 0 || isLoading) && (
        <div className="relative">
          <div
            ref={carouselRef}
            className="h-114 flex gap-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {isLoading
              ? skeletonItems.map((skeleton, index) => (
                  <div key={`skeleton-${index}`} data-carousel-item className="flex-shrink-0">
                    {skeleton.type === 'audio' && <AudioExperienceTileSkeleton />}
                    {skeleton.type === 'video' && <VideoExperienceTileSkeleton />}
                    {skeleton.type === 'resource' && <ResourceTileSkeleton />}
                  </div>
                ))
              : displayItems.map((item) => {
                  if ((item.data as Content)?.video_id) {
                    return (
                      <div key={item.id} data-carousel-item className="flex-shrink-0">
                        <VideoExperienceTile
                          experience={item.data as Content}
                          selectedTagGroupName={selectedTagGroupName}
                        />
                      </div>
                    );
                  }
                  if (item.listener_audio_id && item.data) {
                    return (
                      <div key={item.id} data-carousel-item className="flex-shrink-0">
                        <AudioExperienceTile
                          experience={item.data as Content}
                          selectedTagGroupName={selectedTagGroupName}
                        />
                      </div>
                    );
                  }
                  if (item.resource_id && item.data) {
                    const resource = item.data as Resource;
                    return (
                      <div key={item.id} data-carousel-item className="flex-shrink-0">
                        <ResourceTile
                          id={resource.id}
                          resource={resource}
                          image={resource.image_url}
                          resourceName={resource.name}
                          caption={resource.description}
                          tagGroupNames={(resource as any).tag_group_names}
                          selectedTagGroupName={selectedTagGroupName}
                          isGlobal={!resource.client_id}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
            {!dfMdMedia && hasMoreItems && !isLoading && collection && (
              <div data-carousel-item className="flex-shrink-0">
                <Link
                  to={`${ROUTE_PATH.COLLECTION}/${collection.id}`}
                  className="hover:border-primary bg-gray-light/50 flex h-[303px] w-[150px] flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed border-gray-300 transition-colors hover:bg-gray-100"
                >
                  <ArrowButton direction="right" className="h-8 w-8" />
                  <span className="text-primary font-albert-sans text-lg font-semibold">View All</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
